import React from 'react';
import DataImporter from './index';
import { Accordion, Table } from 'react-bootstrap';
import { createEvents } from '../../api/eventsAPI';
import handleDownloadTemplateHelper from './handleDownloadTemplateHelper';

const allowedEventStatuses = ['In Progress', 'Complete', 'Pending'];
const allowedEventTypes = ['Planned', 'Breakdown', 'Opportune', 'Standby', 'Accident Damage'];

const normalizeDateTime = (value) => {
  if (value === undefined || value === null || value === '') return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }

  if (typeof value === 'number') {
    const fromNumber = new Date(value);
    if (!Number.isNaN(fromNumber.getTime())) {
      return fromNumber.toISOString();
    }
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const direct = new Date(trimmed);
    if (!Number.isNaN(direct.getTime())) {
      return direct.toISOString();
    }

    const replaced = new Date(trimmed.replace(' ', 'T'));
    if (!Number.isNaN(replaced.getTime())) {
      return replaced.toISOString();
    }

    const appended = new Date(`${trimmed}T00:00:00`);
    if (!Number.isNaN(appended.getTime())) {
      return appended.toISOString();
    }
  }

  return null;
};

const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const EventsImporter = ({ onComplete }) => {
  const handleDataImport = async (rows, onProgress) => {
    let count = 0;

    for (const row of rows) {
      try {
        const name = typeof row.name === 'string' ? row.name.trim().slice(0, 100) : row.name;
        const notes =
          row.text === undefined || row.text === null
            ? ''
            : String(row.text).trim().slice(0, 100);

        const payload = {
          name: name ? name.slice(0, 100) : '',
          notes,
          assetId: toNumberOrNull(row.assetId),
          systemId: toNumberOrNull(row.systemId),
          eventStatus: toNumberOrNull(row.eventStatus),
          eventTypeId: toNumberOrNull(row.eventType ?? row.eventTypeId),
          eventLocationId: toNumberOrNull(row.eventLocation ?? row.eventLocationId),
          startDate: normalizeDateTime(row.startDate) ?? row.startDate ?? null,
          endDate: normalizeDateTime(row.endDate) ?? row.endDate ?? null,
          plannedStartDate: normalizeDateTime(row.plannedStartDate) ?? row.plannedStartDate ?? null,
          plannedEndDate: normalizeDateTime(row.plannedEndDate) ?? row.plannedEndDate ?? null,
        };

        await createEvents(payload);
      } catch (err) {
        console.error('Failed to import event', err);
      }

      count += 1;
      if (onProgress) onProgress(count);
    }
  };

  return (
    <>
      <div className='card h-100 p-16 rounded-0'>
        <p>
          Upload an Excel or CSV file to bulk import <strong>events</strong>. The importer validates each
          row, checks lookup values like status and location, and creates events once all rows are valid.
        </p>
        <p>
          Only the event statuses <strong>In Progress</strong> and <strong>Complete</strong> are accepted
          during import. Event types are limited to <strong>Planned</strong>, <strong>Breakdown</strong>,
          <strong>Opportune</strong>, <strong>Standby</strong>, and <strong>Accident Damage</strong>.
        </p>
        <div className='mb-4'>
          <button
            className='btn btn-outline-secondary btn-sm'
            onClick={() => handleDownloadTemplateHelper('events')}
          >
            Download Template
          </button>
        </div>
        <div className='mb-4'>
          <Accordion alwaysOpen={false}>
            <Accordion.Item eventKey='0'>
              <Accordion.Header>Required Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file must include the following column headers:</p>
                <Table bordered size='sm'>
                  <thead className='table-light'>
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>name</td><td>Text (max 100)</td><td>Event title</td></tr>
                    <tr><td>startDate</td><td>Date/DateTime</td><td>Actual start date/time</td></tr>
                    <tr><td>endDate</td><td>Date/DateTime</td><td>Actual end date/time</td></tr>
                    <tr><td>eventStatus</td><td>Lookup</td><td>Status name (In Progress or Complete)</td></tr>
                    <tr><td>eventType</td><td>Lookup</td><td>Type name (Planned, Breakdown, Opportune, Standby or Accident Damage)</td></tr>
                    <tr><td>assetId</td><td>Lookup</td><td>Asset number linked to the event</td></tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey='1'>
              <Accordion.Header>Optional Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file can optionally include these additional columns:</p>
                <Table bordered size='sm'>
                  <thead className='table-light'>
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>notes</td><td>Text (max 255)</td><td>Description or notes</td></tr>
                    <tr><td>systemId</td><td>Lookup</td><td>System name associated with the event</td></tr>
                    <tr><td>plannedStartDate</td><td>Date/DateTime</td><td>Planned start date/time (leave blank if not known)</td></tr>
                    <tr><td>eventLocation</td><td>Lookup</td><td>Location name from the event location list</td></tr>
                    <tr><td>plannedEndDate</td><td>Date/DateTime</td><td>Planned end date/time (leave blank if not known)</td></tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <DataImporter
          entity='events'
          requiredFields={[
            { key: 'name', type: 'string', required: true, maxLength: 100 },
            { key: 'notes', type: 'string', maxLength: 100 },
            { key: 'systemId', type: 'lookup', api: 'asset-system', nameField: 'name' },
            { key: 'startDate', type: 'datetime' },
            { key: 'endDate', type: 'datetime' },
            { key: 'plannedStartDate', type: 'datetime' },
            { key: 'plannedEndDate', type: 'datetime' },
            {
              key: 'eventStatus',
              type: 'lookup',
              required: true,
              api: 'event-status',
              nameField: 'name',
              allowedNames: allowedEventStatuses,
            },
            {
              key: 'eventTypeId',
              type: 'lookup',
              required: true,
              api: 'event-type',
              nameField: 'name',
              allowedNames: allowedEventTypes,
            },
            {
              key: 'eventLocation',
              type: 'lookup',
              api: 'event-location',
              nameField: 'name',
            },
            {
              key: 'assetId',
              type: 'lookup',
              required: true,
              api: 'assets',
              nameField: 'assetNumber',
            },
          ]}
          onCommit={handleDataImport}
          onComplete={onComplete}
        />
      </div>
    </>
  );
};

export default EventsImporter;
